/**
 * Firebase Cloud Messaging configuration for Correly push notifications.
 *
 * Setup:
 * 1. Go to firebase.google.com → Create a project (or use existing)
 * 2. Go to Project Settings → Cloud Messaging → Web Push certificates
 * 3. Copy your config values into .env
 * 4. Enable Cloud Messaging in the Firebase console
 */

// Firebase will be initialized when credentials are provided.
// For now, this module provides the structure and helpers.

let firebaseApp = null;
let messagingInstance = null;

/**
 * Initialize Firebase. Call once at app startup.
 * Returns false if credentials aren't configured yet.
 */
export async function initFirebase() {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

  if (!apiKey || apiKey === 'your-api-key' || !projectId) {
    console.warn('Firebase not configured. Push notifications disabled. Add credentials to .env');
    return false;
  }

  try {
    // Dynamic import to avoid loading Firebase when not configured
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

    const firebaseConfig = {
      apiKey,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };

    firebaseApp = initializeApp(firebaseConfig);
    messagingInstance = getMessaging(firebaseApp);

    return true;
  } catch (err) {
    console.error('Firebase init failed:', err);
    return false;
  }
}

/**
 * Request notification permission and get the FCM token.
 * Returns the token string, or null if permission denied / not available.
 */
export async function requestNotificationPermission() {
  if (!messagingInstance) {
    console.warn('Firebase messaging not initialized');
    return null;
  }

  try {
    const { getToken } = await import('firebase/messaging');
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    });

    return token;
  } catch (err) {
    console.error('Failed to get FCM token:', err);
    return null;
  }
}

/**
 * Listen for foreground messages.
 * @param {function} callback - receives the message payload
 */
export async function onForegroundMessage(callback) {
  if (!messagingInstance) return;

  try {
    const { onMessage } = await import('firebase/messaging');
    onMessage(messagingInstance, (payload) => {
      callback(payload);
    });
  } catch (err) {
    console.error('Failed to set up foreground listener:', err);
  }
}
